import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Checkbox, CircularProgress,
  List, ListItem, ListItemText, ListItemAvatar, Avatar, Typography, Box
} from "@mui/material";

import "./ProductGroups.css";
import useFetch from "../../hooks/useFetch";

interface Product {
  shopify_product_id: string;
  product_title: string;
  product_image_url?: string;
  price?: string;
  option_selections?: { name: string; values: { value: string; variant_ids: string[]; sku?: string }[] }[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  collectionId: number | string;
  alreadyAddedIds?: string[];
  alreadyAddedProducts?: { shopify_product_id: string; shopify_variant_ids: string[] | null }[];
  onSaved: (products: any[]) => void;
}

const AddProductsDialog: React.FC<Props> = ({ open, onClose, collectionId, alreadyAddedIds = [], alreadyAddedProducts = [], onSaved }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Product[]>([]);
  const allSearchedProducts = React.useRef<Record<string, Product>>({});
  const [selected, setSelected] = useState<Record<string, string[] | null>>({});
  const [msg, setMsg] = useState("");


  const { fetchData: saveProducts, isLoading: saving, errorContent: saveErrorContent } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/productgroups/${collectionId}/products`,
    method: "POST",
  });

  const [searchUrl, setSearchUrl] = React.useState("");

  const { fetchData: searchProducts, isLoading: searching, errorContent: searchErrorContent } = useFetch({
    url: searchUrl,
    method: "GET",
  });

  const handleSearch = async () => {
    if (!query.trim()) return;
    setMsg("");
    setSearchUrl(""); // reset first to force useEffect to re-trigger even if same query
    setTimeout(() => {
      setSearchUrl(`${import.meta.env.VITE_API_URL}/productgroups/shopify-products/search?q=${encodeURIComponent(query)}&limit=20`);
    }, 0);
  };

  React.useEffect(() => {
    if (!searchUrl) return;
    searchProducts().then(result => {
      const fetched: Product[] = result?.data?.products || [];
      setResults(fetched);
      fetched.forEach(p => {
        allSearchedProducts.current[p.shopify_product_id] = p;
      });
      if (!result?.success && !searchErrorContent) setMsg(result?.message || "Search failed.");
      else if (fetched.length === 0) setMsg("No products found.");
    });
  }, [searchUrl]);

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      if (id in prev) { const next = { ...prev }; delete next[id]; return next; }
      return { ...prev, [id]: [] }; // [] = all variants, backend handles it
    });
  };

  const toggleVariant = (productId: string, variantIds: string[], allVariantIds: string[] = []) => {
      setSelected(prev => {
        const currentEntry = prev[productId];
        // If all selected (null or []), expand to all variants first before toggling
        const existing: string[] = (currentEntry === null || (Array.isArray(currentEntry) && currentEntry.length === 0))
          ? [...allVariantIds]
          : (currentEntry ?? []) as string[];
        const allSelected = variantIds.every(v => existing.includes(v));
        const updated = allSelected
          ? existing.filter(v => !variantIds.includes(v))
          : Array.from(new Set([...existing, ...variantIds]));
        const allNowSelected = allVariantIds.length > 0 && allVariantIds.every(v => updated.includes(v));
        return { ...prev, [productId]: allNowSelected ? [] : updated };
      });
    };

  const handleSave = async () => {
    if (Object.keys(selected).length === 0) { setMsg("Select at least one product."); return; }
    setMsg("");
    const result = await saveProducts({
      products: Object.entries(selected).map(([id, variantIds]) => {
      const product = allSearchedProducts.current[id];
      const colorOption = product?.option_selections?.find(o => o.name === "Color");
      return {
        shopify_product_id: id,
        option_selections: variantIds === null || (variantIds ?? []).length === 0
        ? []
        : colorOption?.values
            .filter(cv => cv.variant_ids.some(v => (variantIds ?? []).includes(v)))
            .map(cv => ({
              name: "Color",
              values: [{ value: cv.value, variant_ids: cv.variant_ids, selected: true }]
            })) ?? []
      };
    })
    });
    if (result?.success) {
      setMsg(`${result.message}`);
      const addedProducts = (result.data?.added ?? []).map((added: any) => {
        const fullProduct = results.find(r => String(r.shopify_product_id) === String(added.shopify_product_id));
        return fullProduct ? { ...fullProduct, id: added.id } : added;
      });
      setSelected({});
      setResults([]);
      setQuery("");
      onSaved(addedProducts);
      setTimeout(() => {
        setMsg("");
        onClose();
      }, 1000);
    } else 
    setMsg("");
  };

  const handleClose = () => {
    setQuery(""); setResults([]); setSelected({}); setMsg(""); setSearchUrl("");
    allSearchedProducts.current = {};
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth disableScrollLock PaperProps={{ className: "add-products-dialog-paper" }}>
      <DialogTitle>Add Products to Product Groups</DialogTitle>
      <DialogContent dividers className="add-products-dialog-content">
        <div className="add-products-search-row">
          <TextField
            fullWidth size="small" placeholder="Search products..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSearch()}
          />
          <button onClick={handleSearch} disabled={searching} className="collection-button collection-button--search">
            {searching ? <CircularProgress size={18} color="inherit" /> : "Search"}
          </button>
        </div>

        {(msg || saveErrorContent || searchErrorContent) && (
          <Typography variant="body2" mb={1} color="text.secondary">
            {saveErrorContent || searchErrorContent || msg}
          </Typography>
        )}
        
        <List dense className="add-products-list">
          {results.map(product => {
            const alreadyAdded = alreadyAddedIds.includes(String(product.shopify_product_id));
            const selectedVariantsForProduct = selected[product.shopify_product_id];
            const colorOption = product.option_selections?.find(o => o.name === "Color");
            const allProductVariantIds = colorOption?.values.flatMap(v => v.variant_ids) ?? [];
            const isDirectlySelected = product.shopify_product_id in selected && selectedVariantsForProduct === null;
            const isAllColorsSelected = allProductVariantIds.length > 0 && (
              (Array.isArray(selectedVariantsForProduct) && selectedVariantsForProduct.length === 0) ||
              (Array.isArray(selectedVariantsForProduct) && allProductVariantIds.every(v => selectedVariantsForProduct.includes(v)))
            );
            const isProductSelected = alreadyAdded || isDirectlySelected || isAllColorsSelected;
            const isPartiallySelected = !alreadyAdded && Array.isArray(selectedVariantsForProduct) && selectedVariantsForProduct.length > 0;
            return (
              <React.Fragment key={product.shopify_product_id}>
                <ListItem disablePadding
                  className={`add-products-list-item${alreadyAdded ? " add-products-list-item--disabled" : ""}${isPartiallySelected || isProductSelected ? " add-products-list-item--selecting" : ""}`}
                  onClick={() => !alreadyAdded && toggleSelect(product.shopify_product_id)}>
                  <Checkbox
                    checked={isProductSelected}
                    indeterminate={isPartiallySelected && !isAllColorsSelected}
                    disabled={alreadyAdded}
                    size="small"
                    className="themed-checkbox"
                  />
                  <ListItemAvatar>
                    <Avatar src={product.product_image_url} variant="rounded" className="add-products-avatar">
                      {!product.product_image_url && product.product_title?.[0]}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={`${product.product_title}${
                      (() => {
                        const sku = product.option_selections?.find(o => o.name === "Color")?.values?.[0]?.sku;
                        return sku ? ` - ${sku.split("-")[0]}` : "";
                      })()
                    }`}
                    secondary={product.price ? `${product.price}` : undefined}
                  />
                </ListItem>

                {(() => {
                  const colorOption = product.option_selections?.find(o => o.name === "Color");
                  if (!colorOption || colorOption.values.length === 0) return null;
                  const savedEntry = alreadyAddedProducts.find(p => p.shopify_product_id === product.shopify_product_id);
                  const savedVariants = savedEntry?.shopify_variant_ids ?? null;
                  const isAlreadyDirectSelected = alreadyAdded && (savedVariants === null || savedVariants.length === 0);
                  return (
                    <div className={`add-products-color-list${isPartiallySelected || isProductSelected ? " add-products-color-list--selecting" : ""}`}>
                      {colorOption.values.map(cv => {
                        const selectedVariants = selectedVariantsForProduct ?? [];
                        const isColorSelected = isAlreadyDirectSelected ? false :
                          alreadyAdded
                            ? (savedVariants ?? []).some(v => cv.variant_ids.includes(v))
                            : selectedVariantsForProduct === null
                            ? true
                            : (Array.isArray(selectedVariantsForProduct) && selectedVariantsForProduct.length === 0)
                              ? true
                              : (Array.isArray(selectedVariants) && selectedVariants.length > 0 && cv.variant_ids.every(v => selectedVariants.includes(v)));
                        
                        return (
                          <Box
                            key={cv.value}
                            onClick={() => !(alreadyAdded || isAlreadyDirectSelected) && toggleVariant(product.shopify_product_id, cv.variant_ids, allProductVariantIds)}
                            className={`add-products-color-item${(alreadyAdded || isAlreadyDirectSelected) ? " add-products-color-item--disabled" : ""}${isColorSelected ? " add-products-color-item--selected" : ""}`}
                          >
                            <Checkbox checked={isColorSelected} disabled={alreadyAdded || isAlreadyDirectSelected} size="small" className="themed-checkbox add-products-color-checkbox" />
                            <Typography variant="body2" color="text.secondary">{cv.value}</Typography>
                          </Box>
                        );
                      })}
                    </div>
                  );
                })()}
              </React.Fragment>
            );
          })}
        </List>

        {Object.keys(selected).length > 0 && (
          <Typography variant="caption" color="text.secondary">
            {Object.keys(selected).length} product(s) selected
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving} className="themed-cancel-btn" >Cancel</Button>
        <button onClick={handleSave} disabled={saving} className="collection-button">
          {saving ? <CircularProgress size={18} color="inherit" /> : "Add"}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default AddProductsDialog;